import React, { useState, useEffect } from 'react';
import {
    Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Alert, Snackbar, CircularProgress, Avatar, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import { getCategories, getSubCategories, createSubCategory, updateSubCategory, deleteSubCategory } from '../../../services/firestoreService';
import { uploadImage, deleteImage } from '../../../services/storageService';
import { COLORS } from '../../../assets/colors';

function SubCategoriesManager() {
    const [subCategories, setSubCategories] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentSubCategory, setCurrentSubCategory] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        categoryId: '',
        image: '',
        imageFile: null,
        position: 0
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const [subCatsResult, catsResult] = await Promise.all([getSubCategories(), getCategories()]);
        if (subCatsResult.success) setSubCategories(subCatsResult.data);
        if (catsResult.success) setCategories(catsResult.data);
        setLoading(false);
    };

    const handleOpenDialog = (subCategory = null) => {
        if (subCategory) {
            setEditMode(true);
            setCurrentSubCategory(subCategory);
            setFormData({
                name: subCategory.name,
                categoryId: subCategory.categoryId,
                image: subCategory.image,
                imageFile: null,
                position: subCategory.position
            });
        } else {
            setEditMode(false);
            setCurrentSubCategory(null);
            setFormData({ name: '', categoryId: '', image: '', imageFile: null, position: subCategories.length });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData({ name: '', categoryId: '', image: '', imageFile: null, position: 0 });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData({ ...formData, imageFile: file });
        }
    };

    const handleSubmit = async () => {
        if (!formData.name || !formData.categoryId) {
            showSnackbar('Nombre y categoría son requeridos', 'error');
            return;
        }

        setSubmitting(true);
        try {
            let imageUrl = formData.image;

            if (formData.imageFile) {
                const uploadResult = await uploadImage(formData.imageFile, 'RosalStorage/Categories');
                if (uploadResult.success) {
                    imageUrl = uploadResult.url;
                    if (editMode && formData.image && formData.image !== imageUrl) {
                        await deleteImage(formData.image);
                    }
                } else {
                    showSnackbar('Error al subir la imagen', 'error');
                    setSubmitting(false);
                    return;
                }
            }

            const subCategoryData = {
                name: formData.name,
                categoryId: formData.categoryId,
                image: imageUrl,
                position: formData.position
            };

            const result = editMode
                ? await updateSubCategory(currentSubCategory.id, subCategoryData)
                : await createSubCategory(subCategoryData);

            if (result.success) {
                showSnackbar(editMode ? 'Subcategoría actualizada' : 'Subcategoría creada', 'success');
                handleCloseDialog();
                loadData();
            } else {
                showSnackbar('Error al guardar', 'error');
            }
        } catch (error) {
            showSnackbar('Error inesperado', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async (subCategory) => {
        if (window.confirm(`¿Eliminar "${subCategory.name}"?`)) {
            const result = await deleteSubCategory(subCategory.id, subCategory.categoryId);
            if (result.success) {
                if (subCategory.image) await deleteImage(subCategory.image);
                showSnackbar('Subcategoría eliminada', 'success');
                loadData();
            } else {
                showSnackbar('Error al eliminar', 'error');
            }
        }
    };

    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    const getCategoryName = (categoryId) => {
        const category = categories.find(c => c.id === categoryId);
        return category ? category.name : 'Sin categoría';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <Typography variant="h4" sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', color: COLORS.primary }}>
                    Gestión de Subcategorías
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
                    sx={{ background: COLORS.secondary, fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none', borderRadius: '30px' }}>
                    Nueva Subcategoría
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ background: COLORS.secondary }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Imagen</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Nombre</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Categoría</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }}>Posición</TableCell>
                                <TableCell sx={{ color: 'white', fontFamily: 'Quicksand', fontWeight: 'bold' }} align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {subCategories.map((subCat) => (
                                <TableRow key={subCat.id} hover>
                                    <TableCell><Avatar src={subCat.image} sx={{ width: 60, height: 60, borderRadius: 2 }} /></TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}>{subCat.name}</TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand' }}>{getCategoryName(subCat.categoryId)}</TableCell>
                                    <TableCell sx={{ fontFamily: 'Quicksand' }}>{subCat.position}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleOpenDialog(subCat)} sx={{ color: COLORS.secondary }}><EditIcon /></IconButton>
                                        <IconButton onClick={() => handleDelete(subCat)} sx={{ color: '#dc3545' }}><DeleteIcon /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', color: COLORS.primary, display: 'flex', justifyContent: 'space-between' }}>
                    {editMode ? 'Editar Subcategoría' : 'Nueva Subcategoría'}
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ paddingTop: 2 }}>
                        <TextField
                            fullWidth label="Nombre" value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={{ marginBottom: 2 }}
                            InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                            InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />
                        <FormControl fullWidth sx={{ marginBottom: 2 }}>
                            <InputLabel sx={{ fontFamily: 'Quicksand' }}>Categoría</InputLabel>
                            <Select
                                value={formData.categoryId}
                                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                                sx={{ fontFamily: 'Quicksand' }}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id} sx={{ fontFamily: 'Quicksand' }}>{cat.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth type="number" label="Posición" value={formData.position}
                            onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) })}
                            sx={{ marginBottom: 2 }}
                            InputLabelProps={{ sx: { fontFamily: 'Quicksand' } }}
                            InputProps={{ sx: { fontFamily: 'Quicksand' } }}
                        />
                        <Button variant="outlined" component="label" fullWidth sx={{ marginBottom: 2, fontFamily: 'Quicksand', textTransform: 'none' }}>
                            {formData.imageFile ? formData.imageFile.name : 'Seleccionar Imagen'}
                            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </Button>
                        {(formData.image || formData.imageFile) && (
                            <Box sx={{ marginBottom: 2, display: 'flex', justifyContent: 'center' }}>
                                <Avatar src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : formData.image} sx={{ width: 120, height: 120, borderRadius: 2 }} />
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 3 }}>
                    <Button onClick={handleCloseDialog} sx={{ fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none' }}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting}
                        sx={{ background: COLORS.secondary, fontFamily: 'Quicksand', fontWeight: 'bold', textTransform: 'none' }}>
                        {submitting ? 'Guardando...' : (editMode ? 'Actualizar' : 'Crear')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} sx={{ fontFamily: 'Quicksand', fontWeight: 600 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default SubCategoriesManager;
